import React, { useState } from "react";
import {
  Mail,
  FileText,
  Calendar,
  Settings,
  Monitor,
  HardDrive,
  HelpCircle,
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  CheckCircle,
  Send,
  Upload,
  Wrench,
  Shield,
  BarChart3,
  Bell,
  User,
  Globe,
  Server,
  Database,
  Trash2,
} from "lucide-react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Table from "../components/ui/Table";
import Modal from "../components/ui/Modal";
import Badge from "../components/ui/Badge";
import { format } from "date-fns";
import { useData } from "../context/DataContext";
import { Correspondence, Asset, Ticket } from "../context/DataContext";
import { useToast } from "../context/ToastContext";

export default function Admin() {
  const { state, dispatch } = useData();
  const { showSuccess, showError, showInfo } = useToast();
  const [activeTab, setActiveTab] = useState<
    | "correspondence"
    | "assets"
    | "tickets"
    | "calendar"
    | "documents"
    | "system"
  >("correspondence");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCorrespondenceModal, setShowCorrespondenceModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);

  // Handler functions for CRUD operations
  const handleAddCorrespondence = (
    correspondenceData: Omit<Correspondence, "id">
  ) => {
    try {
      const newCorrespondence: Correspondence = {
        ...correspondenceData,
        id: Date.now().toString(),
      };
      dispatch({ type: "ADD_CORRESPONDENCE", payload: newCorrespondence });
      setShowCorrespondenceModal(false);
      showSuccess(
        "Correspondence Added",
        `Correspondence "${newCorrespondence.title}" has been successfully added.`
      );
    } catch (error) {
      showError("Error", "Failed to add correspondence. Please try again.");
    }
  };

  const handleDeleteCorrespondence = (correspondenceId: string) => {
    const correspondence = state.correspondence.find(
      (c) => c.id === correspondenceId
    );
    if (
      correspondence &&
      window.confirm(
        `Are you sure you want to delete the correspondence "${correspondence.title}"?`
      )
    ) {
      try {
        const updatedCorrespondence = state.correspondence.filter(
          (c) => c.id !== correspondenceId
        );
        dispatch({
          type: "SET_CORRESPONDENCE",
          payload: updatedCorrespondence,
        });
        showSuccess(
          "Correspondence Deleted",
          `Correspondence "${correspondence.title}" has been successfully deleted.`
        );
      } catch (error) {
        showError(
          "Error",
          "Failed to delete correspondence. Please try again."
        );
      }
    }
  };

  const handleAddAsset = (assetData: Omit<Asset, "id">) => {
    try {
      const newAsset: Asset = {
        ...assetData,
        id: Date.now().toString(),
      };
      dispatch({ type: "ADD_ASSET", payload: newAsset });
      setShowAssetModal(false);
      showSuccess(
        "Asset Added",
        `Asset "${newAsset.name}" has been successfully added.`
      );
    } catch (error) {
      showError("Error", "Failed to add asset. Please try again.");
    }
  };

  const handleDeleteAsset = (assetId: string) => {
    const asset = state.assets.find((a) => a.id === assetId);
    if (
      asset &&
      window.confirm(
        `Are you sure you want to delete the asset "${asset.name}"?`
      )
    ) {
      try {
        const updatedAssets = state.assets.filter((a) => a.id !== assetId);
        dispatch({ type: "SET_ASSETS", payload: updatedAssets });
        showSuccess(
          "Asset Deleted",
          `Asset "${asset.name}" has been successfully deleted.`
        );
      } catch (error) {
        showError("Error", "Failed to delete asset. Please try again.");
      }
    }
  };

  const handleAddTicket = (ticketData: Omit<Ticket, "id">) => {
    try {
      const newTicket: Ticket = {
        ...ticketData,
        id: Date.now().toString(),
      };
      dispatch({ type: "ADD_TICKET", payload: newTicket });
      setShowTicketModal(false);
      showSuccess(
        "Ticket Created",
        `Support ticket "${newTicket.title}" has been successfully created.`
      );
    } catch (error) {
      showError("Error", "Failed to create ticket. Please try again.");
    }
  };

  const handleDeleteTicket = (ticketId: string) => {
    const ticket = state.tickets.find((t) => t.id === ticketId);
    if (
      ticket &&
      window.confirm(
        `Are you sure you want to delete the ticket "${ticket.title}"?`
      )
    ) {
      try {
        const updatedTickets = state.tickets.filter((t) => t.id !== ticketId);
        dispatch({ type: "SET_TICKETS", payload: updatedTickets });
        showSuccess(
          "Ticket Deleted",
          `Support ticket "${ticket.title}" has been successfully deleted.`
        );
      } catch (error) {
        showError("Error", "Failed to delete ticket. Please try again.");
      }
    }
  };

  // Use data from context, fallback to sample data if empty
  const correspondence =
    state.correspondence.length > 0
      ? state.correspondence
      : [
          {
            id: "1",
            title: "Request for Pilot Training Program",
            type: "incoming" as const,
            reference: "ECAA-2024-001",
            date: "2024-01-15",
            from: "Egyptian Civil Aviation Authority",
            to: "Syndicate of Pilots",
            subject: "Request for Pilot Training Program",
            content:
              "We would like to request information about your pilot training programs...",
            priority: "high" as const,
            status: "draft" as const,
            attachments: [],
          },
          {
            id: "2",
            title: "Monthly Membership Report",
            type: "outgoing" as const,
            reference: "SOP-2024-002",
            date: "2024-01-10",
            from: "Syndicate of Pilots",
            to: "Ministry of Civil Aviation",
            subject: "Monthly Membership Report",
            content: "Please find attached our monthly membership report...",
            priority: "medium" as const,
            status: "sent" as const,
            attachments: ["membership_report.pdf"],
          },
        ];

  const assets =
    state.assets.length > 0
      ? state.assets
      : [
          {
            id: "1",
            name: "Dell OptiPlex 7090",
            type: "hardware" as const,
            category: "Desktop Computer",
            serialNumber: "DL123456789",
            purchaseDate: "2023-06-15",
            cost: 15000,
            status: "active" as const,
            assignedTo: "Admin Office",
            location: "Office Floor 1",
            maintenanceSchedule: [],
          },
          {
            id: "2",
            name: "Microsoft Office 365",
            type: "license" as const,
            category: "Software License",
            serialNumber: "MS-2023-001",
            purchaseDate: "2023-01-01",
            cost: 5000,
            status: "active" as const,
            assignedTo: "All Staff",
            location: "Cloud",
            maintenanceSchedule: [],
          },
        ];

  const tickets =
    state.tickets.length > 0
      ? state.tickets
      : [
          {
            id: "1",
            title: "Printer Not Working",
            description: "The office printer is not responding to print jobs",
            type: "hardware" as const,
            priority: "medium" as const,
            status: "open" as const,
            reporter: "Secretary 1",
            createdDate: "2024-01-20",
            attachments: [],
            comments: [],
          },
          {
            id: "2",
            title: "Email Server Issue",
            description: "Some emails are not being delivered",
            type: "network" as const,
            priority: "high" as const,
            status: "in-progress" as const,
            reporter: "IT Admin",
            assignee: "IT Support",
            createdDate: "2024-01-18",
            attachments: [],
            comments: [],
          },
        ];

  // Calendar events
  const calendarEvents = [
    {
      id: "1",
      title: "Board Meeting",
      date: "2024-02-15",
      time: "10:00 AM",
      type: "meeting",
      attendees: ["Board Members"],
      location: "Conference Room A",
    },
    {
      id: "2",
      title: "IT Maintenance",
      date: "2024-02-20",
      time: "6:00 PM",
      type: "maintenance",
      attendees: ["IT Team"],
      location: "Server Room",
    },
  ];

  // System documents
  const systemDocuments = [
    {
      id: "1",
      title: "IT Security Policy",
      type: "Policy",
      lastModified: "2024-01-15",
      size: "1.2 MB",
      access: "Admin Only",
    },
    {
      id: "2",
      title: "Asset Management Procedures",
      type: "Procedure",
      lastModified: "2024-01-10",
      size: "856 KB",
      access: "Staff",
    },
  ];

  // System status
  const systemStatus = {
    servers: { status: "online", uptime: "99.9%" },
    database: { status: "online", uptime: "99.8%" },
    email: { status: "online", uptime: "99.7%" },
    backup: { status: "online", lastBackup: "2024-01-20" },
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="warning">Pending</Badge>;
      case "processed":
        return <Badge variant="success">Processed</Badge>;
      case "archived":
        return <Badge variant="default">Archived</Badge>;
      case "active":
        return <Badge variant="success">Active</Badge>;
      case "maintenance":
        return <Badge variant="warning">Maintenance</Badge>;
      case "retired":
        return <Badge variant="default">Retired</Badge>;
      case "open":
        return <Badge variant="error">Open</Badge>;
      case "in-progress":
        return <Badge variant="warning">In Progress</Badge>;
      case "resolved":
        return <Badge variant="success">Resolved</Badge>;
      case "closed":
        return <Badge variant="default">Closed</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "high":
        return <Badge variant="error">High</Badge>;
      case "medium":
        return <Badge variant="warning">Medium</Badge>;
      case "low":
        return <Badge variant="success">Low</Badge>;
      default:
        return <Badge variant="default">{priority}</Badge>;
    }
  };

  const correspondenceColumns = [
    {
      key: "reference",
      header: "Reference",
      render: (item: Correspondence) => (
        <div className="font-mono text-sm">{item.reference}</div>
      ),
    },
    {
      key: "subject",
      header: "Subject",
      render: (item: Correspondence) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {item.subject}
          </div>
          <div className="text-sm text-gray-500">
            {item.type === "incoming" ? "From" : "To"}:{" "}
            {item.type === "incoming" ? item.from : item.to}
          </div>
        </div>
      ),
    },
    {
      key: "date",
      header: "Date",
      render: (item: Correspondence) =>
        format(new Date(item.date), "MMM dd, yyyy"),
    },
    {
      key: "priority",
      header: "Priority",
      render: (item: Correspondence) => getPriorityBadge(item.priority),
    },
    {
      key: "status",
      header: "Status",
      render: (item: Correspondence) => getStatusBadge(item.status),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: Correspondence) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Show details modal
            }}
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteCorrespondence(item.id)}
            title="Delete Correspondence"
            className="text-red-600 hover:text-red-800"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const assetColumns = [
    {
      key: "name",
      header: "Asset",
      render: (item: Asset) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{item.name}</div>
          <div className="text-sm text-gray-500">{item.category}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (item: Asset) => (
        <Badge
          variant={
            item.type === "hardware"
              ? "info"
              : item.type === "software"
              ? "default"
              : "info"
          }
        >
          {item.type}
        </Badge>
      ),
    },
    {
      key: "assignedTo",
      header: "Assigned To",
      render: (item: Asset) => item.assignedTo,
    },
    {
      key: "location",
      header: "Location",
      render: (item: Asset) => item.location,
    },
    {
      key: "cost",
      header: "Cost",
      render: (item: Asset) => `EGP ${item.cost.toLocaleString()}`,
    },
    {
      key: "status",
      header: "Status",
      render: (item: Asset) => getStatusBadge(item.status),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: Asset) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Show details modal
            }}
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteAsset(item.id)}
            title="Delete Asset"
            className="text-red-600 hover:text-red-800"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const ticketColumns = [
    {
      key: "title",
      header: "Ticket",
      render: (item: Ticket) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{item.title}</div>
          <div className="text-sm text-gray-500">#{item.id}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (item: Ticket) => <Badge variant="info">{item.type}</Badge>,
    },
    {
      key: "assignee",
      header: "Assigned To",
      render: (item: Ticket) => item.assignee || "Unassigned",
    },
    {
      key: "priority",
      header: "Priority",
      render: (item: Ticket) => getPriorityBadge(item.priority),
    },
    {
      key: "status",
      header: "Status",
      render: (item: Ticket) => getStatusBadge(item.status),
    },
    {
      key: "createdDate",
      header: "Created",
      render: (item: Ticket) =>
        format(new Date(item.createdDate), "MMM dd, yyyy"),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: Ticket) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Show details modal
            }}
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteTicket(item.id)}
            title="Delete Ticket"
            className="text-red-600 hover:text-red-800"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Administrative Operations
          </h1>
          <p className="text-gray-600">
            Manage correspondence, assets, and IT support
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === "correspondence" && (
            <Button onClick={() => setShowCorrespondenceModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Correspondence
            </Button>
          )}
          {activeTab === "assets" && (
            <Button onClick={() => setShowAssetModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Asset
            </Button>
          )}
          {activeTab === "tickets" && (
            <Button onClick={() => setShowTicketModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Ticket
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Correspondence
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {correspondence.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Monitor className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Assets</p>
              <p className="text-2xl font-bold text-gray-900">
                {assets.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <HelpCircle className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Open Tickets</p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  tickets.filter(
                    (t) => t.status === "open" || t.status === "in-progress"
                  ).length
                }
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">
                {tickets.filter((t) => t.status === "resolved").length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "correspondence", name: "Correspondence", icon: Mail },
            { id: "assets", name: "Asset Management", icon: Monitor },
            { id: "tickets", name: "Helpdesk", icon: HelpCircle },
            { id: "calendar", name: "Calendar", icon: Calendar },
            { id: "documents", name: "Documents", icon: FileText },
            { id: "system", name: "System Status", icon: Server },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </Card>

      {/* Tab Content */}
      {activeTab === "correspondence" && (
        <Card padding="none">
          <Table
            data={correspondence}
            columns={correspondenceColumns}
            emptyMessage="No correspondence found"
          />
        </Card>
      )}

      {activeTab === "assets" && (
        <Card padding="none">
          <Table
            data={assets}
            columns={assetColumns}
            emptyMessage="No assets found"
          />
        </Card>
      )}

      {activeTab === "tickets" && (
        <Card padding="none">
          <Table
            data={tickets}
            columns={ticketColumns}
            emptyMessage="No tickets found"
          />
        </Card>
      )}

      {activeTab === "calendar" && (
        <div className="space-y-6">
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Upcoming Events
              </h3>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Event
              </Button>
            </div>
            <div className="space-y-4">
              {calendarEvents.map((event) => (
                <div
                  key={event.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {event.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {format(new Date(event.date), "EEEE, MMMM dd, yyyy")}{" "}
                          at {event.time}
                        </p>
                        <p className="text-sm text-gray-500">
                          {event.location}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge
                        variant={event.type === "meeting" ? "info" : "warning"}
                      >
                        {event.type}
                      </Badge>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {activeTab === "documents" && (
        <div className="space-y-6">
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                System Documents
              </h3>
              <div className="flex gap-2">
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Document
                </Button>
              </div>
            </div>
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Modified
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Access
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {systemDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FileText className="h-5 w-5 text-gray-400 mr-3" />
                          <div className="text-sm font-medium text-gray-900">
                            {doc.title}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="info">{doc.type}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(doc.lastModified), "MMM dd, yyyy")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {doc.size}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge
                          variant={
                            doc.access === "Admin Only" ? "error" : "success"
                          }
                        >
                          {doc.access}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {activeTab === "system" && (
        <div className="space-y-6">
          {/* System Status Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Server className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Servers</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {systemStatus.servers.status}
                  </p>
                  <p className="text-xs text-gray-500">
                    Uptime: {systemStatus.servers.uptime}
                  </p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Database className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Database</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {systemStatus.database.status}
                  </p>
                  <p className="text-xs text-gray-500">
                    Uptime: {systemStatus.database.uptime}
                  </p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Email</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {systemStatus.email.status}
                  </p>
                  <p className="text-xs text-gray-500">
                    Uptime: {systemStatus.email.uptime}
                  </p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <HardDrive className="h-6 w-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Backup</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {systemStatus.backup.status}
                  </p>
                  <p className="text-xs text-gray-500">
                    Last:{" "}
                    {format(new Date(systemStatus.backup.lastBackup), "MMM dd")}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* System Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                System Maintenance
              </h3>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    showInfo(
                      "System Diagnostics",
                      "System diagnostics started. This would run comprehensive system checks and display results."
                    );
                  }}
                >
                  <Wrench className="h-4 w-4 mr-2" />
                  Run System Diagnostics
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    showInfo(
                      "Database Backup",
                      "Database backup initiated. This would create a full backup of the system database."
                    );
                  }}
                >
                  <Database className="h-4 w-4 mr-2" />
                  Backup Database
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    showInfo(
                      "Security Scan",
                      "Security scan started. This would check for vulnerabilities and security issues."
                    );
                  }}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Security Scan
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    showInfo(
                      "Performance Report",
                      "Performance report generated. This would show system performance metrics and recommendations."
                    );
                  }}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Performance Report
                </Button>
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <Bell className="h-4 w-4 mr-2" />
                  Send System Alert
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <User className="h-4 w-4 mr-2" />
                  User Management
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Settings className="h-4 w-4 mr-2" />
                  System Settings
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Globe className="h-4 w-4 mr-2" />
                  Network Status
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Modals */}
      <CorrespondenceModal
        isOpen={showCorrespondenceModal}
        onClose={() => setShowCorrespondenceModal(false)}
        onSubmit={handleAddCorrespondence}
      />
      <AssetModal
        isOpen={showAssetModal}
        onClose={() => setShowAssetModal(false)}
        onSubmit={handleAddAsset}
      />
      <TicketModal
        isOpen={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        onSubmit={handleAddTicket}
      />

      {/* Edit Modals - TODO: Implement these modals */}
    </div>
  );
}

// Modal Components
function CorrespondenceModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    type: "outgoing" as "outgoing" | "incoming",
    reference: "",
    date: new Date().toISOString().split("T")[0],
    from: "Syndicate of Pilots",
    to: "",
    subject: "",
    content: "",
    priority: "medium" as "low" | "medium" | "high",
    status: "draft" as "draft" | "sent" | "received",
    attachments: [] as string[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New Correspondence"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  type: e.target.value as "outgoing" | "incoming",
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="incoming">Incoming</option>
              <option value="outgoing">Outgoing</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  priority: e.target.value as "low" | "medium" | "high",
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <Input
          label="Subject"
          value={formData.subject}
          onChange={(e) =>
            setFormData({ ...formData, subject: e.target.value })
          }
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={formData.type === "incoming" ? "From" : "To"}
            value={formData.type === "incoming" ? formData.from : formData.to}
            onChange={(e) =>
              setFormData({
                ...formData,
                [formData.type === "incoming" ? "from" : "to"]: e.target.value,
              })
            }
            required
          />
          <Input
            label={formData.type === "incoming" ? "To" : "From"}
            value={formData.type === "incoming" ? formData.to : formData.from}
            onChange={(e) =>
              setFormData({
                ...formData,
                [formData.type === "incoming" ? "to" : "from"]: e.target.value,
              })
            }
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <textarea
            value={formData.content}
            onChange={(e) =>
              setFormData({ ...formData, content: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows={6}
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            <Send className="h-4 w-4 mr-2" />
            Send Correspondence
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AssetModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}) {
  const [formData, setFormData] = useState({
    name: "",
    type: "hardware" as
      | "hardware"
      | "software"
      | "furniture"
      | "vehicle"
      | "other",
    category: "",
    assignedTo: "",
    location: "",
    cost: 0,
    purchaseDate: new Date().toISOString().split("T")[0],
    serialNumber: "",
    status: "active" as "active" | "maintenance" | "retired" | "lost",
    warrantyExpiry: "",
    maintenanceSchedule: [] as any[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Asset" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Asset Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  type: e.target.value as
                    | "hardware"
                    | "software"
                    | "furniture"
                    | "vehicle"
                    | "other",
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="hardware">Hardware</option>
              <option value="software">Software</option>
              <option value="vehicle">Vehicle</option>
              <option value="furniture">Furniture</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Assigned To"
            value={formData.assignedTo}
            onChange={(e) =>
              setFormData({ ...formData, assignedTo: e.target.value })
            }
            placeholder="e.g., IT Support, Admin Team"
          />
          <Input
            label="Warranty Expiry"
            type="date"
            value={formData.warrantyExpiry}
            onChange={(e) =>
              setFormData({ ...formData, warrantyExpiry: e.target.value })
            }
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Location"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            required
          />
          <Input
            label="Cost (EGP)"
            type="number"
            value={formData.cost}
            onChange={(e) =>
              setFormData({ ...formData, cost: Number(e.target.value) })
            }
            required
          />
        </div>

        <Input
          label="Purchase Date"
          type="date"
          value={formData.purchaseDate}
          onChange={(e) =>
            setFormData({ ...formData, purchaseDate: e.target.value })
          }
          required
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            <Plus className="h-4 w-4 mr-2" />
            Add Asset
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function TicketModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "technical" as
      | "technical"
      | "hardware"
      | "software"
      | "network"
      | "other",
    priority: "medium" as "low" | "medium" | "high" | "urgent",
    status: "open" as "open" | "in-progress" | "resolved" | "closed",
    reporter: "Current User",
    assignee: "",
    createdDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    resolution: "",
    attachments: [] as string[],
    comments: [] as any[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Support Ticket"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Brief description of the issue"
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="technical">Technical</option>
              <option value="hardware">Hardware</option>
              <option value="software">Software</option>
              <option value="network">Network</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  priority: e.target.value as
                    | "low"
                    | "medium"
                    | "high"
                    | "urgent",
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows={6}
            placeholder="Detailed description of the issue, steps to reproduce, etc."
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            <HelpCircle className="h-4 w-4 mr-2" />
            Create Ticket
          </Button>
        </div>
      </form>
    </Modal>
  );
}
